import { APP_INVENTORY_API_BASE } from "../../../config/api";
/**
 * LocationsList — Locations management page
 *
 * Route: /inventory/locations
 *
 * Full CRUD for physical locations.
 * Uses all 5 location endpoints:
 *   GET    /locations              — list (DataGrid)
 *   POST   /locations              — create (CreateLocationModal)
 *   GET    /locations/:id          — detail (edit drawer pre-fill)
 *   PATCH  /locations/:id          — update (EditLocationDrawer)
 *   DELETE /locations/:id          — deactivate (DeactivateDialog)
 *
 * Features:
 *   - 4 stat cards (total, active, by type breakdown)
 *   - DataGrid: server-side pagination, search, type filter, active filter
 *   - Create modal
 *   - Inline edit drawer (slide from right)
 *   - Deactivate confirmation dialog
 *   - Re-activate toggle via PATCH { is_active: true }
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  Chip,
  Alert,
  Skeleton,
  Stack,
  TextField,
  MenuItem,
  useTheme,
  Collapse,
  Button,
  IconButton,
  Tooltip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Drawer,
  Divider,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import axios from "axios";
import { useAuth } from "../../../auth/useAuth";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

const API_BASE = `${APP_INVENTORY_API_BASE}`;

const LOCATION_TYPES = [
  { value: "WAREHOUSE", label: "Warehouse", color: "#1d4ed8", bg: "#eff6ff" },
  {
    value: "CUSTOMER_SITE",
    label: "Customer site",
    color: "#065f46",
    bg: "#ecfdf5",
  },
  {
    value: "SERVICE_CENTER",
    label: "Service center",
    color: "#7c3aed",
    bg: "#f5f3ff",
  },
  { value: "TRANSIT", label: "Transit", color: "#92400e", bg: "#fff7ed" },
];

const TYPE_MAP = Object.fromEntries(LOCATION_TYPES.map((t) => [t.value, t]));

const EMPTY_FORM = {
  name: "",
  location_type: "WAREHOUSE",
  address: "",
  city: "",
  state: "",
  country: "",
};

// ─── Create Location Modal ────────────────────────────────────────────────────

function CreateLocationModal({ open, onClose, onCreated, token, activeOrg }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setErrors({});
      setSubmitting(false);
      setSubmitErr("");
    }
  }, [open]);

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.location_type) e.location_type = "Type is required";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_BASE}/locations`,
        {
          name: form.name.trim(),
          location_type: form.location_type,
          address: form.address.trim() || undefined,
          city: form.city.trim() || undefined,
          state: form.state.trim() || undefined,
          country: form.country.trim() || undefined,
        },
        { headers: authHeaders },
      );
      onCreated(res.data);
    } catch (err) {
      setSubmitErr(err.response?.data?.error || "Failed to create location");
      setSubmitting(false);
    }
  };

  const f = (field) => ({
    size: "small",
    fullWidth: true,
    value: form[field],
    onChange: (e) => {
      setForm((p) => ({ ...p, [field]: e.target.value }));
      setErrors((p) => ({ ...p, [field]: "" }));
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",

          px: 2.5,
          py: 2,

          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",

          // boxShadow: "0 10px 30px rgba(15, 23, 42, 0.25)",

          color: "#fff", // 🔥 important for text visibility
        }}
      >
        <Typography fontWeight={700} color="#fff">
          Create location
        </Typography>

        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {submitErr && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {submitErr}
          </Alert>
        )}
        <Stack spacing={2.5} pt={0.5}>
          <TextField
            label="Name"
            required
            {...f("name")}
            error={!!errors.name}
            helperText={errors.name || 'e.g. "Mumbai Main Warehouse"'}
          />
          <TextField
            select
            label="Location type"
            required
            {...f("location_type")}
            error={!!errors.location_type}
            helperText={errors.location_type}
          >
            {LOCATION_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
          <Divider />
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={700}
          >
            Address (optional)
          </Typography>
          <TextField label="Street address" {...f("address")} />
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField label="City" {...f("city")} />
            <TextField label="State" {...f("state")} />
          </Box>
          <TextField label="Country" {...f("country")} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <AddIcon />
            )
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,

            // 🔥 Gradient background
            background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
            // color: "#000",

            // Optional hover effect
            "&:hover": {
              background: "linear-gradient(135deg, #FCD34D, #D97706)",
            },

            // Disabled state fix
            "&.Mui-disabled": {
              background: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          {submitting ? "Creating…" : "Create location"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Edit Location Drawer ─────────────────────────────────────────────────────

function EditLocationDrawer({
  location,
  open,
  onClose,
  onUpdated,
  token,
  activeOrg,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (location && open) {
      setForm({
        name: location.name || "",
        location_type: location.location_type || "WAREHOUSE",
        address: location.address || "",
        city: location.city || "",
        state: location.state || "",
        country: location.country || "",
      });
      setError("");
      setSubmitting(false);
    }
  }, [location, open]);

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.patch(
        `${API_BASE}/locations/${location.id}`,
        {
          name: form.name.trim(),
          location_type: form.location_type,
          address: form.address.trim() || null,
          city: form.city.trim() || null,
          state: form.state.trim() || null,
          country: form.country.trim() || null,
        },
        { headers: authHeaders },
      );
      onUpdated(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Update failed");
      setSubmitting(false);
    }
  };

  const f = (field) => ({
    size: "small",
    fullWidth: true,
    value: form[field],
    onChange: (e) => setForm((p) => ({ ...p, [field]: e.target.value })),
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420 },
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          p: 0,
        },
      }}
    >
      <Box
        p={2.5}
        borderBottom="1px solid #eee"
        sx={{
          position: "relative",
          overflow: "hidden",
          mb: 4,
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 2 },

          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",

          boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700} color="#fff">
            Edit location
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
        <Stack spacing={2.5}>
          <TextField label="Name" required {...f("name")} />

          <TextField select label="Location type" {...f("location_type")}>
            {LOCATION_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>

          <Divider />

          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={700}
          >
            Address
          </Typography>

          <TextField label="Street address" {...f("address")} />

          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
            gap={2}
          >
            <TextField label="City" {...f("city")} />
            <TextField label="State" {...f("state")} />
          </Box>

          <TextField label="Country" {...f("country")} />
        </Stack>
      </Box>
      <Box
        p={2}
        borderTop="1px solid #eee"
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        gap={2}
        sx={{
          position: "sticky",
          bottom: 0,
          background: "#fff",
        }}
      >
        <Button onClick={onClose} fullWidth sx={{ textTransform: "none" }}>
          Cancel
        </Button>

        <Button
          fullWidth
          onClick={handleSave}
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={14} sx={{ color: "#000" }} />
            ) : (
              <SaveIcon />
            )
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,

            background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
            color: "#000", // better contrast on yellow

            "&:hover": {
              background: "linear-gradient(135deg, #FCD34D, #D97706)",
            },

            "&:disabled": {
              background: "rgba(255,255,255,0.2)",
              color: "rgba(255,255,255,0.5)",
            },
          }}
        >
          {submitting ? "Saving…" : "Save changes"}
        </Button>
      </Box>
    </Drawer>
  );
}

// ─── Deactivate Dialog ────────────────────────────────────────────────────────

function DeactivateDialog({
  location,
  open,
  onClose,
  onDeactivated,
  token,
  activeOrg,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setSubmitting(false);
      setError("");
    }
  }, [open]);

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  const handleDeactivate = async () => {
    setSubmitting(true);
    try {
      await axios.delete(`${API_BASE}/locations/${location.id}`, {
        headers: authHeaders,
      });
      onDeactivated(location.id);
    } catch (err) {
      setError(err.response?.data?.error || "Deactivation failed");
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle fontWeight={700}>Deactivate location?</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        <DialogContentText>
          <strong>{location?.name}</strong> will be marked inactive. Existing
          movement records are preserved. It will no longer appear in movement
          dropdowns.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disableElevation
          onClick={handleDeactivate}
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <BlockIcon />
            )
          }
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {submitting ? "Deactivating…" : "Deactivate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Custom Toolbar ───────────────────────────────────────────────────────────

function CustomToolbar() {
  return (
    <GridToolbarContainer
      sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
    >
      <Box display="flex" gap={1}>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport csvOptions={{ fileName: "locations" }} />
      </Box>
    </GridToolbarContainer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────

export default function LocationsList() {
  const theme = useTheme();
  const { permissions, activeOrg } = useAuth();
  const token = localStorage.getItem("authToken");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [successAlert, setSuccessAlert] = useState("");

  // Modal / drawer state
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const normalize = (val) =>
    (val || "").toString().toLowerCase().replace(/_/g, " ").trim();
  // Stats
  const [stats, setStats] = useState({ total: 0, active: 0, loading: true });

  const hasPermission = (p) => {
    if (!permissions || permissions.size === 0) return false;
    if (permissions.has("*")) return true;
    return permissions.has(p);
  };

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  const showSuccess = (msg) => {
    setSuccessAlert(msg);
    setTimeout(() => setSuccessAlert(""), 5000);
  };

  // ── Fetch locations ──
  const fetchLocations = useCallback(async () => {
    if (!hasPermission("inventory.location.view")) return;
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        ...(search && { q: search }),
        ...(typeFilter && { location_type: typeFilter }),
        ...(activeFilter !== "" && { is_active: activeFilter }),
      };
      const res = await axios.get(`${API_BASE}/locations/${activeOrg.id}`, {
        headers: authHeaders,
        params,
      });
      setLocations(res.data.data || []);
      setRowCount(res.data.meta?.pagination?.total_items || 0);
      setError(null);
    } catch {
      setError("Failed to load locations.");
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg, paginationModel, search, typeFilter, activeFilter]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // ── Fetch stats ──
  const fetchStats = useCallback(() => {
    if (!hasPermission("inventory.location.view")) return;
    Promise.all([
      axios.get(`${API_BASE}/locations/${activeOrg.id}`, {
        headers: authHeaders,
        params: { per_page: 1 },
      }),
      axios.get(`${API_BASE}/locations/${activeOrg.id}`, {
        headers: authHeaders,
        params: { per_page: 1, is_active: "true" },
      }),
    ])
      .then(([total, active]) =>
        setStats({
          total: total.data.meta?.pagination?.total_items ?? 0,
          active: active.data.meta?.pagination?.total_items ?? 0,
          loading: false,
        }),
      )
      .catch(() => setStats((prev) => ({ ...prev, loading: false })));
  }, [token, activeOrg]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // ── Event handlers ──
  const handleCreated = (location) => {
    setCreateOpen(false);
    fetchLocations();
    fetchStats();
    showSuccess(`Location "${location.name}" created successfully.`);
  };

  const handleUpdated = (location) => {
    setEditTarget(null);
    setLocations((prev) =>
      prev.map((l) => (l.id === location.id ? location : l)),
    );
    showSuccess(`Location "${location.name}" updated.`);
  };

  const handleDeactivated = (id) => {
    setDeactivateTarget(null);
    setLocations((prev) =>
      prev.map((l) => (l.id === id ? { ...l, is_active: false } : l)),
    );
    fetchStats();
    showSuccess("Location deactivated.");
  };

  const handleReactivate = async (location) => {
    try {
      const res = await axios.patch(
        `${API_BASE}/locations/${location.id}`,
        { is_active: true },
        { headers: authHeaders },
      );
      setLocations((prev) =>
        prev.map((l) => (l.id === location.id ? res.data : l)),
      );
      fetchStats();
      showSuccess(`Location "${location.name}" reactivated.`);
    } catch (err) {
      setError(err.response?.data?.error || "Reactivation failed");
    }
  };

  const clearFilters = () => {
    setSearch("");
    setTypeFilter("");
    setActiveFilter("");
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const hasFilters = search || typeFilter || activeFilter !== "";

  // ── Columns ──
  const columns = [
    {
      field: "serialIndex",
      headerName: "S.No",
      width: 64,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {paginationModel.page * paginationModel.pageSize +
              params.api.getRowIndexRelativeToVisibleRows(params.id) +
              1}
          </Typography>
        </Box>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>
            {params.value}
          </Typography>
          {params.row.city && (
            <Typography variant="caption" color="text.secondary">
              {[params.row.city, params.row.state, params.row.country]
                .filter(Boolean)
                .join(", ")}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "location_type",
      headerName: "Type",
      width: 140,
      renderCell: (params) => {
        const t = TYPE_MAP[params.value] || {
          label: params.value,
          color: "#374151",
          bg: "#f3f4f6",
        };
        return (
          <Chip
            label={t.label}
            size="small"
            sx={{
              bgcolor: t.bg,
              color: t.color,
              fontWeight: 600,
              borderRadius: "6px",
              fontSize: 11,
            }}
          />
        );
      },
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            {params.value || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "country",
      headerName: "Country",
      width: 110,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            {params.value || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 100,
      renderCell: (params) =>
        params.value ? (
          <Chip
            label="Active"
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontSize: 11 }}
          />
        ) : (
          <Chip
            label="Inactive"
            size="small"
            variant="outlined"
            sx={{ fontSize: 11, color: "text.secondary" }}
          />
        ),
    },
    {
      field: "_actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Stack direction="row" spacing={0.5}>
            {hasPermission("inventory.location.update") && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditTarget(params.row);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {hasPermission("inventory.location.delete") &&
              params.row.is_active && (
                <Tooltip title="Deactivate">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeactivateTarget(params.row);
                    }}
                  >
                    <BlockIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            {hasPermission("inventory.location.update") &&
              !params.row.is_active && (
                <Tooltip title="Reactivate">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReactivate(params.row);
                    }}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
          </Stack>
        </Box>
      ),
    },
  ];

  if (!hasPermission("inventory.location.view")) {
    return (
      <Box p={4}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          You do not have permission to view locations.
        </Alert>
      </Box>
    );
  }

  const statCards = [
    {
      title: "Total locations",
      value: stats.total,
      bg: "#f1f5f9",
      icon: <LocationOnIcon color="action" />,
    },
    {
      title: "Active",
      value: stats.active,
      bg: "#ecfdf5",
      icon: <CheckCircleIcon sx={{ color: "#065f46" }} />,
    },
    {
      title: "Inactive",
      value: stats.total - stats.active,
      bg: "#fff5f5",
      icon: <BlockIcon sx={{ color: "#dc2626" }} />,
    },
  ];
  const filteredLocations = locations.filter((row) => {
    const query = normalize(searchText);

    return (
      normalize(row.name).includes(query) ||
      normalize(row.location_type).includes(query) ||
      normalize(row.address).includes(query) ||
      normalize(row.city).includes(query) ||
      normalize(row.state).includes(query) ||
      normalize(row.country).includes(query) ||
      normalize(row.is_active ? "active" : "inactive").includes(query)
    );
  });
  return (
    <Box
      sx={{
        minHeight: "100%",
        pt: { xs: 5, sm: 5, md: 3 },
        pb: 4,
        px: { xs: 2, md: 3 },

        background:
          "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #f8fbff 0%, #eef4f8 100%)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          mb: 4,
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 4 },
          borderRadius: 5,

          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",

          boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
        }}
      >
        {/* ── Header ── */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          mb={4}
        >
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ color: "#e2f8f5" ,fontSize:{xs:28,md:40} }}>
              Locations
            </Typography>
            <Typography variant="body2" color="#fff">
              Warehouses, customer sites, service centers and transit hubs.
            </Typography>
          </Box>
          {hasPermission("inventory.location.create") && (
            <Button
              disableElevation
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{
                textTransform: "none",
                px: 2.2,
                py: 1.1,
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 700,
                whiteSpace: "nowrap",
                color: "#082F49",
                background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
              }}
            >
              Add location
            </Button>
          )}
        </Stack>

        {/* ── Alerts ── */}
        <Collapse in={!!successAlert}>
          <Alert
            severity="success"
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setSuccessAlert("")}
          >
            {successAlert}
          </Alert>
        </Collapse>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setError(null)}
            action={
              <Button color="inherit" size="small" onClick={fetchLocations}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* ── Stat cards ── */}
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "1fr", // mobile → 1 card
            sm: "1fr 1fr", // tablet → 2 cards
            md: "1fr 1fr 1fr", // small laptop → 3 cards
            lg: "1fr 1fr 1fr", // desktop → 3 cards in one row
          }}
          gap={2}
          mb={1}
        >
          {stats.loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <Card
                  key={i}
                  elevation={0}
                  sx={{
                    borderRadius: 7,
                    p: 2,

                    backgroundColor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    backdropFilter: "blur(12px)",

                    color: "#fff",

                    transition: "all 0.25s ease",

                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 15px 40px rgba(0,0,0,0.25)",
                    },
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={52}
                    height={52}
                    sx={{ mr: 2, borderRadius: 2 }}
                  />
                  <Box flex={1}>
                    <Skeleton variant="text" width="60%" sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="40%" height={32} />
                  </Box>
                </Card>
              ))
            : statCards.map((s, i) => (
                <Card
                  key={i}
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    p: 2,

                    // 🔥 FIXED BACKGROUND (no more white)
                    backgroundColor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    backdropFilter: "blur(12px)",

                    color: "#fff",

                    transition: "all 0.25s ease",
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 2,
                        bgcolor: s.bg,
                        mr: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {s.icon}
                    </Box>
                    <Box>
                      <Typography variant="body2" color="#fff" fontWeight={500}>
                        {s.title}
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {Math.max(0, s.value).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
        </Box>
      </Box>
      {/* ── Table card ── */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 5,
          overflow: "hidden",

          // 🔥 glass theme
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.12)",
          backdropFilter: "blur(14px)",

          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
      >
        {/* Filter bar */}
        <Box
          p={2}
          borderBottom="1px solid"
          borderColor="divider"
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          gap={2}
          sx={{
            p: 2,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,

            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",

            borderBottom: "1px solid rgba(255,255,255,0.15)",
          }}
        >
          {/* Type filter */}
          <TextField
            select
            size="small"
            label="Type"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            sx={{
              width: { xs: "100%", md: 160 },
              borderRadius: 1,
              "& .MuiInputLabel-root": { color: "rgba(203, 213, 225, 0.75)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5eead4" },
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiSelect-icon": { color: "rgba(203, 213, 225, 0.70)" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterListIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="">All types</MenuItem>
            {LOCATION_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>

          {/* Active filter */}
          <TextField
            select
            size="small"
            label="Status"
            value={activeFilter}
            onChange={(e) => {
              setActiveFilter(e.target.value);
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            sx={{
              width: { xs: "100%", md: 160 },
              borderRadius: 1,
              "& .MuiInputLabel-root": { color: "rgba(203, 213, 225, 0.75)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5eead4" },
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiSelect-icon": { color: "rgba(203, 213, 225, 0.70)" },
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </TextField>

          {hasFilters && (
            <Button
              size="small"
              variant="outlined"
              onClick={clearFilters}
              sx={{
                textTransform: "none",
                fontSize: 12,
                height: 38,

                color: "#fff", // text color
                borderColor: "rgba(255,255,255,0.4)", // border

                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              Clear filters
            </Button>
          )}
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            sx={{
              width: { xs: "100%", sm: 200 },
              ml: "auto",
              borderRadius: 2,

              "& .MuiInputLabel-root": { color: "rgba(203, 213, 225, 0.75)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5eead4" },
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiSelect-icon": { color: "rgba(203, 213, 225, 0.70)" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#fff" }} />
                </InputAdornment>
              ),

              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchText("");
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                    sx={{ color: "#fff" }}
                  >
                    ✕
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* DataGrid */}
        <Box
          sx={{
            height: locations.length === 0 && !loading ? 320 : 560,
            width: "100%",
          }}
        >
          <DataGrid
            rows={filteredLocations}
            rowCount={rowCount}
            loading={loading}
            paginationMode="server"
            pageSizeOptions={[15, 20, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            columns={columns}
            getRowId={(row) => row.id}
            slots={{
              toolbar: CustomToolbar,
              noRowsOverlay: () => (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  py={6}
                >
                  <LocationOnIcon
                    sx={{ fontSize: 52, color: "text.disabled", mb: 2 }}
                  />
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="text.secondary"
                    gutterBottom
                  >
                    {hasFilters
                      ? "No locations match your filters"
                      : "No locations yet"}
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    {hasFilters
                      ? "Try clearing the filters"
                      : 'Click "Add location" to create the first one'}
                  </Typography>
                </Box>
              ),
            }}
            disableRowSelectionOnClick
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f8fafc",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                color: "text.secondary",
                fontSize: 12,
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "#334155" : "#f8fafc",
              },
              "& .MuiDataGrid-cell:focus": { outline: "none" },
              "& .MuiDataGrid-cell:focus-within": { outline: "none" },
            }}
          />
        </Box>
      </Card>

      {/* ── Dialogs / Drawers ── */}
      <CreateLocationModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
        token={token}
        activeOrg={activeOrg}
      />

      <EditLocationDrawer
        location={editTarget}
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        onUpdated={handleUpdated}
        token={token}
        activeOrg={activeOrg}
      />

      {deactivateTarget && (
        <DeactivateDialog
          location={deactivateTarget}
          open={!!deactivateTarget}
          onClose={() => setDeactivateTarget(null)}
          onDeactivated={handleDeactivated}
          token={token}
          activeOrg={activeOrg}
        />
      )}
    </Box>
  );
}
